/**
 * Player Intelligence — Mi rendimiento (Fase 1).
 *
 * Componente REUTILIZABLE que sustituye el placeholder de "Estadísticas"
 * dentro de la ficha pública del jugador. Se apoya EXCLUSIVAMENTE en la
 * prop `data` y NO contiene ningún dato específico de ningún jugador.
 *
 * Contrato de la prop `data` (ver `lib/fixtures/mario_player_intelligence.js`):
 *   { player_matches: PlayerMatch[] }
 *
 * Estados visuales (spec Fase 1):
 *   A. 0 partidos publicables (OK|PARTIAL) → sólo estado vacío.
 *   B. 1 partido publicable                → último análisis + 4 KPIs LW/RW
 *                                            + mini heatmap + "Ver análisis
 *                                            completo".
 *   C. >=2 partidos publicables            → todo lo anterior + "Ver
 *                                            evolución".
 *
 * Reglas críticas:
 *   - Ningún KPI se sustituye si viene `null`; se oculta manteniendo layout.
 *   - `0` es valor válido: se muestra "0".
 *   - Heatmap: `positions_xy` → SVG generado; fallback `heatmap_url`.
 *     Sin ninguno de los dos, se oculta sin placeholder inventado.
 *   - No se renderiza tendencia, resumen, fortalezas, foco ni interpretaciones.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BarChart3, ArrowRight, LineChart, CalendarClock, Timer, MapPin } from 'lucide-react';
import { ROUTES } from '@/lib/constants';
import {
  selectKpisForPlayerMatch,
  pickLatestPublishedPlayerMatch,
  filterPublishablePlayerMatches,
  formatMatchDateEs,
  resolveDataQualityStatus,
} from '@/lib/playerIntelligence';
import { HeatmapImage } from '@/components/players/HeatmapImage';

const TID = {
  root: 'player-intelligence-performance',
  empty: 'player-intelligence-empty',
  latest: 'player-intelligence-latest',
  matchDate: 'player-intelligence-match-date',
  matchOpponent: 'player-intelligence-match-opponent',
  matchPosition: 'player-intelligence-match-position',
  matchMinutes: 'player-intelligence-match-minutes',
  kpiGrid: 'player-intelligence-kpis',
  kpiItem: (k) => `player-intelligence-kpi-${k}`,
  heatmap: 'player-intelligence-heatmap',
  ctaFull: 'player-intelligence-cta-full-analysis',
  ctaEvolution: 'player-intelligence-cta-evolution',
  qualityBadge: 'player-intelligence-quality-badge',
};

export function PlayerIntelligencePerformance({ data }) {
  const playerMatches = Array.isArray(data?.player_matches) ? data.player_matches : [];
  const publishable = useMemo(
    () => filterPublishablePlayerMatches(playerMatches),
    [playerMatches],
  );
  const latest = useMemo(
    () => pickLatestPublishedPlayerMatch(playerMatches),
    [playerMatches],
  );

  return (
    <Card
      data-testid={TID.root}
      className="p-5 sm:p-6 rounded-2xl mt-6 bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
    >
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="h-4 w-4 text-[var(--ns-gold-600)]" />
        <h2 className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
          Mi rendimiento
        </h2>
      </div>

      {publishable.length === 0 || !latest ? (
        <EmptyState />
      ) : (
        <LatestBlock
          playerMatch={latest}
          totalPublishable={publishable.length}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Estado A: sin partidos publicables
// ---------------------------------------------------------------------------
function EmptyState() {
  return (
    <div
      data-testid={TID.empty}
      className="p-6 rounded-xl border border-dashed border-[var(--ns-border-strong)] bg-[var(--ns-bg-2)]/40 text-center"
    >
      <p className="text-sm text-[var(--ns-text-muted)]">
        Todavía no hay partidos analizados.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estados B/C: último análisis + KPIs + heatmap + CTAs
// ---------------------------------------------------------------------------
function LatestBlock({ playerMatch, totalPublishable }) {
  const kpis = useMemo(
    () => selectKpisForPlayerMatch(playerMatch).filter((k) => k.formatted !== null),
    [playerMatch],
  );
  const showEvolution = totalPublishable >= 2;
  const quality = resolveDataQualityStatus(playerMatch);
  const dateLabel = formatMatchDateEs(playerMatch?.match?.date);
  const opponent = playerMatch?.match?.opponent || null;
  const position = playerMatch?.player?.position || null;
  const minutes = playerMatch?.player?.minutes_played;
  const playerId = playerMatch?.player_id;
  const matchId = playerMatch?.player_match_id;

  return (
    <div data-testid={TID.latest} className="flex flex-col gap-5">
      {/* Cabecera "Último análisis" */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-[var(--ns-text-subtle)] font-semibold">
            Último análisis
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
            {dateLabel ? (
              <span
                data-testid={TID.matchDate}
                className="inline-flex items-center gap-1.5 text-sm text-[var(--ns-text)]"
              >
                <CalendarClock className="h-3.5 w-3.5 text-[var(--ns-gold-700)]" />
                {dateLabel}
              </span>
            ) : null}
            {opponent ? (
              <span
                data-testid={TID.matchOpponent}
                className="text-sm text-[var(--ns-text)]"
              >
                vs {opponent}
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-[var(--ns-text-muted)]">
            {position ? (
              <span data-testid={TID.matchPosition} className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3 text-[var(--ns-text-subtle)]" />
                Posición jugada: <strong className="text-[var(--ns-text)]">{position}</strong>
              </span>
            ) : null}
            {minutes !== null && minutes !== undefined ? (
              <span data-testid={TID.matchMinutes} className="inline-flex items-center gap-1">
                <Timer className="h-3 w-3 text-[var(--ns-text-subtle)]" />
                {minutes}′
              </span>
            ) : null}
          </div>
        </div>
        <QualityBadge status={quality} />
      </header>

      {/* KPIs (0..4 tarjetas — nunca se sustituyen) */}
      {kpis.length > 0 ? (
        <div
          data-testid={TID.kpiGrid}
          className="grid grid-cols-2 md:grid-cols-4 gap-3"
        >
          {kpis.map((k) => (
            <KpiTile key={k.key} testId={TID.kpiItem(k.key)} label={k.label} value={k.formatted} />
          ))}
        </div>
      ) : null}

      {/*
        Simplificación UI (microfase): mini heatmap oculto del resumen.
        El componente `MiniHeatmap` y sus datos (positioning.positions_xy /
        heatmap_url) permanecen intactos para reactivación futura sin cambios
        en el contrato de datos.
      */}
      {/* <MiniHeatmap positioning={playerMatch?.positioning} /> */}

      {/* CTA — único botón: "Ver análisis completo".
          `showEvolution`/`ctaEvolution` se mantienen en la lógica del
          componente pero no se renderizan en esta fase. */}
      {matchId ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            asChild
            data-testid={TID.ctaFull}
            className="h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
          >
            <Link to={ROUTES.playerIntelligenceMatch(playerId, matchId)}>
              Ver análisis completo
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function QualityBadge({ status }) {
  const map = {
    OK: {
      label: 'OK',
      cls: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    },
    PARTIAL: {
      label: 'Parcial',
      cls: 'bg-amber-50 text-amber-800 border-amber-200',
    },
    REVIEW: {
      label: 'Revisión',
      cls: 'bg-rose-50 text-rose-800 border-rose-200',
    },
  };
  const cfg = map[status] || map.REVIEW;
  return (
    <Badge
      data-testid={TID.qualityBadge}
      className={`h-6 rounded-full text-[10px] uppercase tracking-wider border ${cfg.cls}`}
    >
      {cfg.label}
    </Badge>
  );
}

function KpiTile({ testId, label, value }) {
  return (
    <div
      data-testid={testId}
      className="p-3 rounded-xl border border-[var(--ns-border)] bg-[var(--ns-amber-50)]/40"
    >
      <p className="text-[10px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
        {label}
      </p>
      <p className="text-2xl font-display text-[var(--ns-text)] mt-1 leading-none">
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mini heatmap — SVG determinista a partir de positions_xy (0..1 normalizado).
// Sin librerías. Sin colores fuera del sistema. Sin placeholder inventado.
// ---------------------------------------------------------------------------
function MiniHeatmap({ positioning }) {
  const xy = Array.isArray(positioning?.positions_xy) ? positioning.positions_xy : null;
  const url = positioning?.heatmap_url || null;
  const hasXy = xy && xy.length > 0;

  if (!hasXy && !url) return null;

  return (
    <div data-testid={TID.heatmap} className="rounded-xl border border-[var(--ns-border)] overflow-hidden bg-[var(--ns-bg-2)]/40">
      <div className="px-3 py-2 flex items-center gap-2 border-b border-[var(--ns-border)]">
        <p className="text-[10px] uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">
          Posicionamiento
        </p>
      </div>
      {hasXy ? (
        <HeatmapSvg points={xy} />
      ) : (
        <HeatmapImage
          url={url}
          className="w-full"
          maxHeightPx={224}
          fallbackImgClassName="w-full h-auto object-contain bg-black/5"
        />
      )}
    </div>
  );
}

/**
 * SVG mínimo: pintamos el campo y los puntos como blobs semitransparentes.
 * Coordenadas normalizadas: x∈[0,1] (izq→der), y∈[0,1] (defensa→ataque).
 */
function HeatmapSvg({ points }) {
  const W = 320;
  const H = 200;
  // Filtrar puntos válidos
  const pts = points
    .filter((p) => Array.isArray(p) && p.length === 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .map(([x, y]) => [Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y))]);
  if (pts.length === 0) return null;
  return (
    <svg
      role="img"
      aria-label="Mini heatmap de posiciones"
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto max-h-56 block"
    >
      {/* Campo */}
      <rect x="0" y="0" width={W} height={H} fill="#eef7ee" />
      <rect x="4" y="4" width={W - 8} height={H - 8} fill="none" stroke="#7fb87f" strokeWidth="1.5" />
      <line x1={W / 2} y1="4" x2={W / 2} y2={H - 4} stroke="#7fb87f" strokeWidth="1" />
      <circle cx={W / 2} cy={H / 2} r="18" fill="none" stroke="#7fb87f" strokeWidth="1" />
      {/* Blobs */}
      {pts.map(([x, y], i) => {
        // y=0 defensa (arriba SVG), y=1 ataque (abajo SVG) — invertimos para
        // pintar el ataque hacia la derecha del campo horizontal.
        const cx = 6 + y * (W - 12); // eje largo → ataque a la derecha
        const cy = 6 + (1 - x) * (H - 12); // eje corto → banda izquierda arriba
        return (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r="14"
            fill="var(--ns-gold-600, #b45309)"
            opacity="0.28"
          />
        );
      })}
    </svg>
  );
}
